import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function ARangeSection() {
  const [activeFeature, setActiveFeature] = useState(0);

  const features = [
    {
      icon: 'ri-computer-line',
      title: 'Productivity-Focused Design',
      description: 'Optimised desk layouts, ergonomic workspace, and professional meeting setups for maximum efficiency'
    },
    {
      icon: 'ri-plug-line',
      title: 'Business-Ready Infrastructure',
      description: 'High-speed data connectivity, reliable power systems, and professional-grade ventilation'
    },
    {
      icon: 'ri-timer-flash-line',
      title: 'Rapid Deployment',
      description: 'Fast installation and scalable solutions perfect for growing businesses and temporary offices'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Professional Environment',
      description: 'Sound insulation, climate control, and architectural design that inspires focus and creativity'
    }
  ];

  const useCases = [
    {
      title: 'Backyard Office',
      icon: 'ri-home-office-line',
      image: '/images/336d158546b0.webp'
    },
    {
      title: 'Remote Work Studio',
      icon: 'ri-computer-line',
      image: '/images/e1fa3474346d.webp'
    },
    {
      title: 'Construction Site',
      icon: 'ri-building-line',
      image: '/images/ai-modern-modular-office-cabin-at-construct-51ad1e.webp'
    },
    {
      title: 'Sales Center',
      icon: 'ri-store-line',
      image: '/images/ai-modern-sales-office-cabin-with-professio-ebf689.webp'
    },
    {
      title: 'Business Park',
      icon: 'ri-community-line',
      image: '/images/915e876bd1a3.webp'
    },
    {
      title: 'Resort Office',
      icon: 'ri-hotel-line',
      image: '/images/ai-modern-office-cabin-at-resort-location-w-ac8087.webp'
    }
  ];

  return (
    <section id="a-range" className="relative py-16 sm:py-24 lg:py-32 overflow-hidden bg-gradient-to-b from-transparent to-[#0A0A0A] w-full">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        {/* Section Header */}
        <div className="text-center mb-12 sm:mb-16 lg:mb-20">
          <div className="inline-flex items-center gap-3 px-4 sm:px-6 py-2 sm:py-3 bg-gray-500/10 border border-gray-500/30 rounded-full mb-4 sm:mb-6">
            <div className="w-6 h-6 sm:w-8 sm:h-8 bg-gray-500/20 rounded-full flex items-center justify-center">
              <span className="text-base sm:text-lg font-bold text-gray-300">A</span>
            </div>
            <span className="text-gray-300 text-xs sm:text-sm uppercase tracking-widest">A SERIES</span>
          </div>
          <h2 className="text-3xl sm:text-5xl lg:text-6xl font-thin mb-4 sm:mb-6 px-4">
            Smart Modular <span className="text-gray-300">Office Spaces</span>
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-gray-400 max-w-3xl mx-auto px-4">
            Apple-cabin-style modular spaces designed for productivity, work, and commercial applications
          </p>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center mb-12 sm:mb-16 lg:mb-20 w-full">
          {/* Left: Features */}
          <div className="space-y-4 sm:space-y-6 order-2 lg:order-1 w-full">
            {features.map((feature, index) => (
              <div
                key={index}
                onMouseEnter={() => setActiveFeature(index)}
                onClick={() => setActiveFeature(index)}
                className={`p-6 sm:p-8 rounded-2xl border transition-all duration-300 cursor-pointer w-full ${
                  activeFeature === index
                    ? 'bg-gray-500/10 border-gray-400/50 shadow-[0_0_30px_rgba(148,163,184,0.2)]'
                    : 'bg-white/5 border-white/10 hover:border-gray-500/30'
                }`}
              >
                <div className="flex items-start gap-4 sm:gap-6">
                  <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center transition-all flex-shrink-0 ${
                    activeFeature === index ? 'bg-gray-500/20' : 'bg-white/10'
                  }`}>
                    <i className={`${feature.icon} text-xl sm:text-2xl ${activeFeature === index ? 'text-gray-300' : 'text-gray-400'}`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg sm:text-xl font-medium mb-2 text-white">{feature.title}</h3>
                    <p className="text-sm sm:text-base text-gray-400 leading-relaxed">{feature.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Right: Image */}
          <div className="relative order-1 lg:order-2 w-full">
            <div className="absolute inset-0 bg-gradient-to-r from-gray-500/20 to-slate-500/20 blur-3xl rounded-full"></div>
            <div className="relative w-full h-[400px] sm:h-[500px] lg:h-[600px]">
              <img 
                src="/images/336d158546b0.webp" 
                alt="A Range Office Cabin"
                className="w-full h-full object-contain lg:object-cover rounded-3xl"
              />
            </div>
          </div>
        </div>

        {/* Use Cases Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-8 sm:mb-12 lg:mb-16 w-full">
          {useCases.map((useCase, index) => (
            <div key={index} className="group relative bg-gradient-to-br from-gray-500/5 to-slate-500/5 backdrop-blur-xl border border-gray-500/20 rounded-2xl overflow-hidden hover:border-gray-400 hover:shadow-[0_0_30px_rgba(148,163,184,0.2)] transition-all duration-500 cursor-pointer w-full">
              <div className="relative w-full h-40 sm:h-48">
                <img 
                  src={useCase.image}
                  alt={useCase.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
              </div>
              <div className="p-4 sm:p-6">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-500/20 rounded-xl flex items-center justify-center mb-3 sm:mb-4">
                  <i className={`${useCase.icon} text-xl sm:text-2xl text-gray-300`}></i>
                </div>
                <h3 className="text-base sm:text-lg font-medium text-white">{useCase.title}</h3>
              </div>
            </div>
          ))}
        </div>

        {/* CTA Button */}
        <div className="text-center w-full flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link
            to="/a-range-details"
            className="group inline-flex items-center gap-3 px-8 py-4 bg-gray-300 text-black text-base font-medium rounded-full hover:bg-gray-200 transition-all hover:shadow-[0_0_30px_rgba(148,163,184,0.6)] whitespace-nowrap cursor-pointer"
          >
            Explore A Series Details
            <div className="w-8 h-8 bg-black rounded-full flex items-center justify-center group-hover:shadow-[0_0_15px_rgba(148,163,184,0.4)] transition-all">
              <i className="ri-arrow-right-line text-white"></i>
            </div>
          </Link>
          <Link to="/contact?model=A%20Series" className="group inline-flex items-center gap-3 px-8 py-4 bg-gray-500 text-white text-base font-medium rounded-full hover:bg-gray-400 transition-all hover:shadow-[0_0_30px_rgba(148,163,184,0.6)] whitespace-nowrap cursor-pointer">
            Request Quote for A Series
            <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center group-hover:shadow-[0_0_15px_rgba(148,163,184,0.4)] transition-all">
              <i className="ri-mail-send-line text-gray-700"></i>
            </div>
          </Link>
        </div>
      </div>
    </section>
  );
}
